import React from "react";
import DownArrow from "~/components/HeaderMaximus/DownArrow";
import {
  IconContainer,
  IconDropdown,
  IconLabel,
  IconSVG,
} from "~/components/HeaderMaximus/Icon";
import { Cart as TCart } from "~/entities/cart/types";
import useBreakpoints from "~/hooks/useBreakpoints";
import { formatCurrency } from "~/utils/number";

interface DropdownContentsProps {
  cart: TCart | null;
}

const DropdownContents: React.FC<DropdownContentsProps> = ({ cart }) => {
  const quantity = cart?.quantity || 0;
  const discount = cart?.totals.discount || 0;
  const bulkDiscount = cart?.totals.bulkDiscount || 0;
  const items = cart?.lineItems || [];
  const lineItemTotal = cart?.totals.lineItemTotal || 0;

  const handleProceedToCheckoutClick = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    window.location.href = "/cart";
  };

  return (
    <div
      className={`flex flex-col items-stretch px-7 pt-0.5 ${quantity > 0 ? "pb-8" : "pb-[337px]"} text-[#565757]`}
    >
      <div className="mb-2.5 mt-1 flex items-center justify-between text-base">
        <div>
          Total Items: <strong>{quantity}</strong>
        </div>
        <div>
          Subtotal: <strong>{formatCurrency(lineItemTotal)}</strong>
        </div>
      </div>
      <hr />
      {items.length ? (
        <ul className="max-h-[calc(min(40vh,250px))] w-full flex-shrink-0 overflow-y-auto p-1 pt-3">
          {items.map((item) => (
            <li
              key={`cart-item-${item.id}`}
              className="mb-2 flex min-h-[125px] overflow-hidden border-b border-b-[#D6D6D6] py-3 last:border-b-0"
            >
              <div className="mr-2.5 flex w-[80px] items-start justify-center">
                <img
                  className="max-h-full max-w-full"
                  alt=""
                  src={item.image}
                />
              </div>
              <div className="w-full min-w-0 text-[#616161] md:flex-grow">
                <a
                  tabIndex={0}
                  href={item.path}
                  className="mb-4 block overflow-hidden text-ellipsis whitespace-nowrap text-blue400 hover:underline"
                >
                  {item.name}
                </a>
                <span className="mb-2 inline-block w-full">
                  Price:{" "}
                  <strong className="text-[#373737]">
                    {formatCurrency(parseFloat(item.price))}
                  </strong>
                </span>
                <span className="mb-2 mr-2 inline-block">
                  Qty:{" "}
                  <strong className="text-[#373737]">{item.quantity}</strong>
                </span>
                <span className="mb-2 inline-block">
                  Size: <strong className="text-[#373737]">{item.size}</strong>
                </span>
                <span className="inline-block w-full">
                  Color:{" "}
                  <strong className="text-[#373737]">{item.color}</strong>
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="py-7 text-center text-[#727779]">
          Add some items to your cart!
        </div>
      )}
      {discount !== 0 || bulkDiscount !== 0 ? (
        <div className="border-t border-t-[#D6D6D6] pb-1 pt-4">
          {discount !== 0 ? (
            <div className="flex flex-col">
              <div className="mb-1 w-full self-end  text-right text-base">
                Full Price Total: {formatCurrency(lineItemTotal + discount)}
              </div>
              <div className="mb-2 self-end text-base">
                Savings:{" "}
                <span className="text-red">-{formatCurrency(discount)}</span>
              </div>
            </div>
          ) : null}
          {bulkDiscount !== 0 ? (
            <div className="flex flex-col">
              <div className="mb-1 w-full self-end text-right text-base">
                Product Total: {formatCurrency(lineItemTotal)}
              </div>
              <div className="mb-2 self-end text-base">
                Bulk Savings:{" "}
                <span className="text-red">
                  -{formatCurrency(bulkDiscount)}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      <hr />
      <div className="mb-4 mt-3 self-end text-base">
        Subtotal: {formatCurrency(lineItemTotal)}
      </div>
      <a
        onClick={handleProceedToCheckoutClick}
        href="/cart"
        tabIndex={0}
        className="h-[32px] w-[280.188px] self-center text-nowrap rounded-md bg-blue px-[15px] text-center text-[1.125rem] uppercase leading-[1.75] text-white hover:bg-blue200"
        type="button"
      >
        Proceed to Secure Checkout
      </a>
    </div>
  );
};

interface Props {
  isDropdownActive: boolean;
  setIsDropdownActive: (value: boolean) => void;
  cart: TCart | null;
}

const Cart: React.FC<Props> = ({
  cart,
  isDropdownActive,
  setIsDropdownActive,
}) => {
  const { isMobile, isDesktop } = useBreakpoints();

  const handleMouseLeave = () => {
    if (isMobile) return;
    setIsDropdownActive(false);
  };

  const handleMouseEnter = () => {
    if (isMobile) return;
    setIsDropdownActive(true);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (isDesktop) {
      // Using window location for now instead of useNavigate because we
      // want to force server side navigation
      window.location.href = "/cart";
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setIsDropdownActive(!isDropdownActive);
  };

  const handleCloseClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    event.preventDefault();
    setIsDropdownActive(false);
  };

  return (
    <IconContainer
      title={`Cart with ${cart?.quantity || 0} items`}
      href="/cart"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <IconSVG>
        <g>
          <path d="M7.01,14.75h10.51" />
          <path d="M5.94,14.79c-.78,0-1.22-.63-.83-1.36,0-.05,.05-.05,.05-.1,.58-.78,.49-1.61,.29-2.53-.68-2.92-1.31-5.84-1.95-8.81-.05-.1-.05-.24-.1-.34H.93C.54,1.65,.2,1.51,.05,1.12-.14,.63,.2,0,.78,0h3.41c.39,0,.63,.24,.73,.58,.1,.24,.1,.54,.19,.83,.05,.1,.15,.15,.24,.19,.05,.05,.15,0,.24,0h14.6c.88,0,1.17,.29,1.17,1.17v5.94c0,.78-.29,1.02-1.07,1.12-1.65,.19-3.26,.39-4.87,.58-1.61,.15-3.21,.34-4.82,.54-1.12,.1-2.24,.24-3.41,.39,.19,.58,.29,1.12-.15,1.7h11.53c.19,0,.39,0,.58,.05,.29,.15,.54,.49,.49,.83-.05,.39-.29,.63-.68,.73-.15,.05-.29,.05-.49,.1l-12.56,.05Z" />
          <circle
            cx="6.48"
            cy="16.74"
            r="1.65"
          />
          <circle
            cx="18.01"
            cy="16.74"
            r="1.65"
          />
        </g>
      </IconSVG>
      {cart?.quantity ? (
        <span
          className={`absolute right-[-10px] top-[-7px] z-[100] block h-[17px] min-w-[17px] max-w-[35px] overflow-hidden text-ellipsis rounded-[10px] bg-[#565757] px-1 text-center text-[11px] font-bold leading-[1rem] text-white [text-rendering:geometricPrecision] md:right-2 md:bg-[#047aca]`}
        >
          {cart?.quantity}
        </span>
      ) : null}
      <IconLabel>
        Cart
        <DownArrow />
      </IconLabel>
      <IconDropdown
        title="Cart"
        isActive={isDropdownActive}
        onCloseClick={handleCloseClick}
      >
        <DropdownContents cart={cart} />
      </IconDropdown>
    </IconContainer>
  );
};

export default Cart;
